import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useGuildConfig, useGuildOverview, useGuildPatch } from '../../../lib/auditQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';

export const Route = createFileRoute('/guilds/$guildId/')({
  component: GuildOverview,
});

// SPEC §9.2: Overview shows members / active auto-mod rules / infractions
// (last 30 days), plus quick toggles for the four feature flags.
function GuildOverview() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const overview = useGuildOverview(guildId);
  const config = useGuildConfig(guildId);
  const patch = useGuildPatch(guildId);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header title={config.data?.name ?? guildId} />
      <main className="mx-auto max-w-6xl px-6 py-8">
        <section className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-400">
            {t('overview.stats.title')}
          </h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard
              label={t('overview.stats.members')}
              value={overview.data?.memberCount}
              loading={overview.isLoading}
            />
            <StatCard
              label={t('overview.stats.automodRules')}
              value={overview.data?.activeAutomodRules}
              loading={overview.isLoading}
            />
            <StatCard
              label={t('overview.stats.infractions30d')}
              value={overview.data?.infractionsLast30d}
              loading={overview.isLoading}
            />
          </div>
        </section>

        <section className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-400">
            {t('overview.toggles.title')}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <ToggleCard
              label={t('overview.toggles.automod')}
              hint={t('overview.toggles.automodHint')}
              checked={config.data?.automodEnabled ?? false}
              disabled={!config.data || patch.isPending}
              onChange={(v) => {
                patch.mutate({ automodEnabled: v });
              }}
              linkTo="/guilds/$guildId/automod"
              guildId={guildId}
              linkLabel={t('overview.toggles.configure')}
            />
            <ToggleCard
              label={t('overview.toggles.audit')}
              hint={t('overview.toggles.auditHint')}
              checked={config.data?.auditLogEnabled ?? false}
              disabled={!config.data || patch.isPending}
              onChange={(v) => {
                patch.mutate({ auditLogEnabled: v });
              }}
              linkTo="/guilds/$guildId/audit"
              guildId={guildId}
              linkLabel={t('overview.toggles.configure')}
            />
            <ToggleCard
              label={t('overview.toggles.welcome')}
              hint={t('overview.toggles.welcomeHint')}
              checked={config.data?.welcomeEnabled ?? false}
              disabled={!config.data || patch.isPending}
              onChange={(v) => {
                patch.mutate({ welcomeEnabled: v });
              }}
              linkTo="/guilds/$guildId/welcome"
              guildId={guildId}
              linkLabel={t('overview.toggles.configure')}
            />
            <ToggleCard
              label={t('overview.toggles.goodbye')}
              hint={t('overview.toggles.goodbyeHint')}
              checked={config.data?.goodbyeEnabled ?? false}
              disabled={!config.data || patch.isPending}
              onChange={(v) => {
                patch.mutate({ goodbyeEnabled: v });
              }}
              linkTo="/guilds/$guildId/welcome"
              guildId={guildId}
              linkLabel={t('overview.toggles.configure')}
            />
          </div>
        </section>

        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-400">
            {t('overview.sections.title')}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/automod"
              label={t('overview.sections.automod')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/automations"
              label={t('overview.sections.automations')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/spam"
              label={t('overview.sections.spam')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/spam-queue"
              label={t('overview.sections.spamQueue')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/auto-role"
              label={t('overview.sections.autoRole')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/audit"
              label={t('overview.sections.audit')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/welcome"
              label={t('overview.sections.welcome')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/infractions"
              label={t('overview.sections.infractions')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/members"
              label={t('overview.sections.members')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/reaction-roles"
              label={t('overview.sections.reactionRoles')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/polls"
              label={t('overview.sections.polls')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/purge"
              label={t('overview.sections.purge')}
            />
            <SectionLink
              guildId={guildId}
              to="/guilds/$guildId/api-keys"
              label={t('overview.sections.apiKeys')}
            />
          </div>
        </section>
      </main>
    </div>
  );
}

function Header({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <header className="border-b border-zinc-800">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
        <Link to="/" className="shrink-0">
          <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
        </Link>
        <Breadcrumbs items={[{ label: t('app.name'), to: '/' }, { label: title }]} />
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}

function StatCard({
  label,
  value,
  loading,
}: {
  label: string;
  value: number | undefined;
  loading: boolean;
}) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
      <p className="text-xs uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="mt-2 text-2xl font-semibold tabular-nums">
        {loading ? '…' : (value ?? 0).toLocaleString()}
      </p>
    </div>
  );
}

function ToggleCard({
  label,
  hint,
  checked,
  disabled,
  onChange,
  linkTo,
  guildId,
  linkLabel,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
  linkTo: '/guilds/$guildId/automod' | '/guilds/$guildId/audit' | '/guilds/$guildId/welcome';
  guildId: string;
  linkLabel: string;
}) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
      <label className="flex flex-1 cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
          checked={checked}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.checked);
          }}
        />
        <div className="min-w-0">
          <p className="text-sm font-medium">{label}</p>
          <p className="mt-1 text-xs text-zinc-500">{hint}</p>
        </div>
      </label>
      <Link
        to={linkTo}
        params={{ guildId }}
        className="shrink-0 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
      >
        {linkLabel}
      </Link>
    </div>
  );
}

function SectionLink({
  guildId,
  to,
  label,
}: {
  guildId: string;
  to:
    | '/guilds/$guildId/automod'
    | '/guilds/$guildId/automations'
    | '/guilds/$guildId/api-keys'
    | '/guilds/$guildId/spam'
    | '/guilds/$guildId/spam-queue'
    | '/guilds/$guildId/auto-role'
    | '/guilds/$guildId/audit'
    | '/guilds/$guildId/welcome'
    | '/guilds/$guildId/infractions'
    | '/guilds/$guildId/members'
    | '/guilds/$guildId/reaction-roles'
    | '/guilds/$guildId/polls'
    | '/guilds/$guildId/purge';
  label: string;
}) {
  return (
    <Link
      to={to}
      params={{ guildId }}
      className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-5 py-4 text-sm font-medium hover:bg-zinc-900/70"
    >
      {label} →
    </Link>
  );
}
